import { CloseOutlined } from '@ant-design/icons';
import { IdTag } from '@horof/shared';
import { App, Button, Checkbox, ConfigProvider, Drawer, Input } from 'antd';
import { type ReactNode, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';

/**
 * A table with a side panel, the open row in the address (`?panel=<id>` or
 * `new`). Leaving a panel with unsaved edits asks first.
 */
export function useSplit() {
  const { modal } = App.useApp();
  const [params, setParams] = useSearchParams();
  const panel = params.get('panel');
  const dirty = useRef(false);
  const onDirtyChange = useCallback((value: boolean) => {
    dirty.current = value;
  }, []);

  const go = useCallback(
    async (id: string | null, extra: Record<string, string | null> = {}) => {
      if (dirty.current) {
        const ok = await modal.confirm({
          title: 'تعديلات غير محفوظة',
          content: 'في اللوحة تعديلات لم تُحفظ. هل تريد تجاهلها؟',
          okText: 'تجاهلها',
          okButtonProps: { danger: true },
          cancelText: 'رجوع',
        });
        if (!ok) return;
        dirty.current = false;
      }
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          for (const [k, v] of Object.entries({ ...extra, panel: id })) {
            if (v) next.set(k, v);
            else next.delete(k);
          }
          return next;
        },
        { replace: true },
      );
    },
    [modal, setParams],
  );

  return { panel, open: go, close: () => go(null), onDirtyChange };
}

/** Reports whether the form differs from what is saved. */
export function useDirty(
  values: unknown,
  saved: unknown,
  onDirtyChange: (dirty: boolean) => void,
) {
  const dirty = JSON.stringify(values) !== JSON.stringify(saved);
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  return dirty;
}

/**
 * The panel's frame: title, ×, and the keys (Esc closes, Ctrl+S saves).
 * `inline`: the same content inside a record's page (doc 58), with no title,
 * × or Esc; the page shows them.
 */
export function DeskPanel({
  title,
  recordId,
  onClose,
  onSave,
  inline = false,
  children,
}: {
  title: ReactNode;
  /** The record's id, to copy (doc 56); none for a new one. */
  recordId?: string | null;
  onClose: () => void;
  onSave?: () => void;
  inline?: boolean;
  children: ReactNode;
}) {
  const saveRef = useRef(onSave);
  saveRef.current = onSave;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const inlineRef = useRef(inline);
  inlineRef.current = inline;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.code === 'KeyS') {
        e.preventDefault();
        saveRef.current?.();
      } else if (
        !inlineRef.current &&
        e.key === 'Escape' &&
        // An open list or dialog closes first.
        !document.querySelector(
          '.ant-select-dropdown:not(.ant-select-dropdown-hidden), .ant-modal-wrap, .ant-image-preview-wrap',
        )
      ) {
        closeRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (inline) return <div className="desk-inline">{children}</div>;

  return (
    <DeskDrawer>
      <div className="desk-panel-head">
        <strong>{title}</strong>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={onClose}
          aria-label="إغلاق (Esc)"
          title="إغلاق (Esc)"
        />
      </div>
      <div className="desk-panel-body">
        {recordId && (
          <div className="desk-panel-id">
            <IdTag id={recordId} link />
          </div>
        )}
        {children}
      </div>
    </DeskDrawer>
  );
}

/** Drawers open without animation: switching rows remounts the panel. */
const still = { token: { motion: false } };
const moving = { token: { motion: true } };

/**
 * A record over its list (doc 59): an antd drawer on the table's end, below
 * the header, without a mask, so the table keeps its width and its rows can
 * still be clicked. The panel inside handles Esc and Ctrl+S.
 */
export function DeskDrawer({
  label,
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  return (
    <ConfigProvider theme={still}>
      <Drawer
        open
        placement="left"
        mask={false}
        closable={false}
        keyboard={false}
        autoFocus={false}
        width="clamp(440px, 40vw, 560px)"
        rootClassName="desk-drawer-root"
        styles={{ body: { padding: 0 } }}
      >
        <ConfigProvider theme={moving}>
          <aside className="desk-panel" aria-label={label}>
            {children}
          </aside>
        </ConfigProvider>
      </Drawer>
    </ConfigProvider>
  );
}

/** «تحتاج مراجعة» and what to check. */
export function ReviewBox({
  needsReview,
  reviewNote,
  onChange,
}: {
  needsReview: boolean;
  reviewNote: string;
  onChange: (patch: { needsReview?: boolean; reviewNote?: string }) => void;
}) {
  return (
    <div className={`desk-review${needsReview ? ' is-on' : ''}`}>
      <Checkbox
        checked={needsReview}
        onChange={(e) => onChange({ needsReview: e.target.checked })}
      >
        تحتاج مراجعة
      </Checkbox>
      {needsReview && (
        <Input
          placeholder="ما الذي يجب التحقق منه؟"
          value={reviewNote}
          onChange={(e) => onChange({ reviewNote: e.target.value })}
        />
      )}
    </div>
  );
}

/** A saved document (receipt, slip, invoice): the image or the PDF itself. */
export function DocumentViewer({
  photo,
  title,
}: {
  photo: { url: string; contentType: string };
  title: string;
}) {
  if (photo.contentType === 'application/pdf') {
    return (
      <div className="desk-receipt">
        <iframe src={photo.url} title={title} />
        <a href={photo.url} target="_blank" rel="noreferrer">
          فتح الملف في نافذة جديدة
        </a>
      </div>
    );
  }
  return (
    <div className="desk-receipt">
      <img src={photo.url} alt={title} />
      <a href={photo.url} target="_blank" rel="noreferrer">
        فتح الصورة في نافذة جديدة
      </a>
    </div>
  );
}
